import React, { useEffect, useId, useRef, useState } from 'react';
import Hls from 'hls.js';
import './SeriesDetailHeader.css';
import { getSeriesStatusColor } from '../../utils/seriesStatus';
import { formatSeriesAudience } from '../../utils/seriesAudience';

const display = value => value === null || value === undefined || value === '' ? '—' : value;
const yesNo = value => value === null || value === undefined || value === '' ? '—' : Number(value) === 1 ? 'Yes' : 'No';
const dateTime = value => {
  if (!value) return 'Not set';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString([], {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

function Field({ label, children }) {
  return <div className="series-meta-item"><dt className="series-meta-label">{label}</dt><dd className="series-meta-value">{children}</dd></div>;
}

function Chips({ value }) {
  const values = String(value || '').split(',').map(item => item.trim()).filter(Boolean);
  return values.length ? <span className="series-detail-tags">{values.map((item, index) => <span className="series-tag-chip" key={index}>{item}</span>)}</span> : '—';
}

export default function SeriesDetailHeader({ series, editButton }) {
  const [expanded, setExpanded] = useState(false);
  const descriptionId = useId();
  const videoRef = useRef(null);
  const videoUrl = series.cover_video_raw || series.cover_video || series.cover_video_hls_url;
  const transcoding = series.cover_video_transcoding_status;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoUrl || transcoding === 'processing' || transcoding === 'failed') return;
    let hls;
    if (videoUrl.includes('.m3u8') && Hls.isSupported()) {
      hls = new Hls({ startLevel: -1 });
      hls.loadSource(videoUrl);
      hls.attachMedia(video);
    } else {
      video.src = videoUrl;
    }
    return () => { hls?.destroy(); };
  }, [videoUrl, transcoding]);

  return <>
    <header className="series-page-heading">
      <span className="series-meta-label">Series details</span>
      <h1>{series.title || 'Untitled series'}</h1>
    </header>
    <section className="card series-detail-header mb-3" aria-label="Series information">
      <div className="card-body">
        <div className="series-detail-toolbar">
          <h2>Series information</h2>
          {editButton}
        </div>
        <div className="series-detail-layout">
          <div className="series-detail-info">
            <div className="series-description">
              <span className="series-meta-label">Description</span>
              <p id={descriptionId} className={`series-detail-desc${expanded ? ' is-expanded' : ''}`} title={series.description || undefined}>{display(series.description)}</p>
              {series.description && <button type="button" className="series-description-toggle" aria-expanded={expanded} aria-controls={descriptionId} onClick={() => setExpanded(value => !value)}>{expanded ? 'Show less' : 'Read full description'}</button>}
            </div>
            <dl className="series-detail-meta">
              <Field label="Status"><span className={`series-status-${getSeriesStatusColor(series.status)}`}><span className="series-status-badge"><span className="series-status-dot" aria-hidden="true" />{series.status || 'Unknown'}</span></span></Field>
              <Field label="Audience">{formatSeriesAudience(series)}</Field>
              <Field label="Language">{display(series.language_name)}</Field>
              <Field label="Age rating">{display(series.age_rating)}</Field>
              <Field label="Total episodes">{display(series.total_episode)}</Field>
              <Field label="Free episodes">{display(series.free_episodes)}</Field>
              <Field label="Free">{yesNo(series.is_free)}</Field>
              <Field label="Recommended">{yesNo(series.is_recommended)}</Field>
              <Field label="Publish at">{dateTime(series.publish_at)}</Field>
              <Field label="Unpublish at">{dateTime(series.unpublish_at)}</Field>
            </dl>
            <dl className="series-detail-taxonomy">
              <Field label="Categories"><Chips value={series.category_names ?? series.category_name} /></Field>
              <Field label="Genres"><Chips value={series.tag_names ?? series.tag_name} /></Field>
              <Field label="Tags"><Chips value={series.type_name} /></Field>
            </dl>
          </div>
          <div className="series-detail-images" aria-label="Series media">
            <figure className="series-detail-img-wrap">
              <figcaption className="series-detail-img-label">Thumbnail</figcaption>
              {series.thumbnail ? <img src={series.thumbnail} alt={`${series.title} thumbnail`} className="series-detail-thumb" /> : <div className="series-media-empty">No thumbnail</div>}
            </figure>
            <figure className="series-detail-img-wrap">
              <figcaption className="series-detail-img-label">Poster</figcaption>
              {series.poster ? <img src={series.poster} alt={`${series.title} poster`} className="series-detail-poster" /> : <div className="series-media-empty">No poster</div>}
            </figure>
            <figure className="series-detail-img-wrap">
              <figcaption className="series-detail-img-label">Cover video</figcaption>
              {transcoding === 'processing' ? <div className="series-cover-transcoding" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /><span>Transcoding to HLS…</span></div>
                : transcoding === 'failed' ? <div className="series-cover-transcoding series-cover-transcoding--failed">Transcoding failed<span className="series-cover-transcoding-sub">Please re-upload the video</span></div>
                : videoUrl ? <video ref={videoRef} className="series-detail-cover-video" aria-label="Cover video" controls preload="metadata" playsInline />
                : <div className="series-media-empty">No cover video</div>}
            </figure>
          </div>
        </div>
      </div>
    </section>
  </>;
}
